import { useCallback } from 'react';

/**
 * Returns a memoized handler function that makes an element behave like a regular link.
 * Assumes event is the first argument and forwards all arguments to the linkFn.
 *
 * @param {Function|string} urlOrFn - Either a URL string or a function that generates a URL
 * @returns {Function} Memoized handler function
 */
export const useLinkHandler = (urlOrFn) => {
  return useCallback(
    (event, ...args) => {
      const url = typeof urlOrFn === 'function' ? urlOrFn(event, ...args) : urlOrFn;

      if (!url) throw new Error('No url provided!');

      // Create anchor element
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.style.display = 'none';

      // Add to DOM (some browsers require it)
      document.body.appendChild(anchor);

      try {
        // Forward the event from the original
        const syntheticEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey,
          metaKey: event.metaKey,
          button: event.button,
          buttons: event.buttons,
          view: window,
          detail: 1,
        });

        // Dispatch the event on the anchor to use native behavior
        anchor.dispatchEvent(syntheticEvent);
      } finally {
        // Finally remove anchor element
        document.body.removeChild(anchor);
      }

      // Prevent default behavior
      event.preventDefault();
    },
    [urlOrFn],
  );
};
